import { Grid, Heading, Icon, Text } from '@afterpaytouch/core'
import React, { ReactElement } from 'react'
import { CloseCheckout, DockedFooterPortal, Head } from '../../component'
import { TrackPageViewed } from '../../integrations/monitoring'
import { CheckoutPage } from '../../model'
import { PageNames } from '../../model/monitoring'
import { useCheckoutError, useCheckoutState, useMerchantDisplayNameOrName } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import {
  CASH_COHORT_CONVERGENCE_INSTRUCTION_ICON_NAMES,
  CashCohortConvergenceInstructionKey,
  getCashCohortConvergenceInstructionKeys,
  getCashCohortConvergenceRecognizedViolations,
} from '../../utils/checkoutErrors/cashCohortConvergenceRemediation'
import styles from './style.module.css'

type CashCohortConvergencePageKind = 'instructions' | 'terminal'

interface CashCohortConvergencePageProps {
  kind: CashCohortConvergencePageKind
}

interface CashCohortConvergenceInstructionCopy {
  title: string
  subtext: string
}

/* eslint-disable @afterpay/i18n-require-context */
const CashCohortConvergencePage: CheckoutPage<CashCohortConvergencePageProps> = ({ kind }): ReactElement => {
  const { t } = useTranslation(['common', 'cashCohortConvergenceRemediation'])
  const checkoutError = useCheckoutError()
  const { checkout } = useCheckoutState()
  const merchantDisplayNameOrName = useMerchantDisplayNameOrName()
  // This is a failed/ineligible checkout, so exit to the merchant's failure URL like TerminalError does;
  // useCloseCheckout falls back to the default callback when no failure URL is set.
  const failureCallbackUrl = checkout?.merchant?.failureCallbackUrl
  const { buttonSize, headingSize, textSize } = useCheckoutSizes()
  const isInstructionsPage = kind === 'instructions'
  const heading = isInstructionsPage ? t('cashCohortConvergenceRemediation:instructions.heading') : t('cashCohortConvergenceRemediation:terminal.heading')
  const pageName = isInstructionsPage ? PageNames.CASH_APP_PAY_REMEDIATION : PageNames.CASH_APP_PAY_UNAVAILABLE
  const violations = checkoutError?.extra?.cashCohortConvergenceViolations ?? []
  const recognizedViolations = getCashCohortConvergenceRecognizedViolations(violations)
  const hasRecognizedViolations = recognizedViolations.length > 0
  // Pass the raw violations (not just the recognized ones) so an unrecognized code still appends the
  // generic "finish setup" tile; the page falls back to default copy only when nothing is recognized.
  const instructionKeys = [...new Set(getCashCohortConvergenceInstructionKeys(violations))]
  const getInstructionCopy = (instructionKey: CashCohortConvergenceInstructionKey): CashCohortConvergenceInstructionCopy => {
    switch (instructionKey) {
      case 'cashCohortConvergenceRemediation:instructions.acquireLinkedDebitCard':
        return {
          title: t('cashCohortConvergenceRemediation:instructions.acquireLinkedDebitCard.title'),
          subtext: t('cashCohortConvergenceRemediation:instructions.acquireLinkedDebitCard.subtext'),
        }
      case 'cashCohortConvergenceRemediation:instructions.acquireSmsAlias':
        return {
          title: t('cashCohortConvergenceRemediation:instructions.acquireSmsAlias.title'),
          subtext: t('cashCohortConvergenceRemediation:instructions.acquireSmsAlias.subtext'),
        }
      case 'cashCohortConvergenceRemediation:instructions.acquireEmailAlias':
        return {
          title: t('cashCohortConvergenceRemediation:instructions.acquireEmailAlias.title'),
          subtext: t('cashCohortConvergenceRemediation:instructions.acquireEmailAlias.subtext'),
        }
      case 'cashCohortConvergenceRemediation:instructions.idv':
        return {
          title: t('cashCohortConvergenceRemediation:instructions.idv.title'),
          subtext: t('cashCohortConvergenceRemediation:instructions.idv.subtext'),
        }
      case 'cashCohortConvergenceRemediation:instructions.collectAddress':
        return {
          title: t('cashCohortConvergenceRemediation:instructions.collectAddress.title'),
          subtext: t('cashCohortConvergenceRemediation:instructions.collectAddress.subtext'),
        }
      default:
        return {
          title: t('cashCohortConvergenceRemediation:instructions.generic.title'),
          subtext: t('cashCohortConvergenceRemediation:instructions.generic.subtext'),
        }
    }
  }

  return (
    <>
      <Head title={heading} />
      <div className={styles.page}>
        {merchantDisplayNameOrName.length > 0 && (
          <div className={styles.merchantRow} data-testid='cash-cohort-convergence-merchant-row'>
            <Grid>
              <Grid.Item>
                <span className={styles.merchantName} data-testid='cash-cohort-convergence-merchant-name'>
                  {merchantDisplayNameOrName}
                </span>
              </Grid.Item>
            </Grid>
          </div>
        )}
        <div className={styles.main}>
          <Grid>
            <Grid.Item>
              <section
                className={styles.content}
                data-testid={isInstructionsPage ? 'cash-cohort-convergence-instructions-page' : 'cash-cohort-convergence-terminal-page'}
              >
                {!isInstructionsPage && (
                  <span className={styles.badge} data-testid='cash-cohort-convergence-terminal-icon'>
                    <Icon name='AlertFill' color='Error' size='Large' />
                  </span>
                )}
                <div className={styles.header}>
                  <Heading size={headingSize} renderAs='h1' testNameSpace='cash-cohort-convergence-heading'>
                    {heading}
                  </Heading>
                  {isInstructionsPage && hasRecognizedViolations && (
                    <Text renderAs='p' size={textSize} color='Subtle' data-testid='cash-cohort-convergence-instructions-subtitle'>
                      {t('cashCohortConvergenceRemediation:instructions.subtitle')}
                    </Text>
                  )}
                </div>
                {isInstructionsPage && recognizedViolations.length === 0 && (
                  <Text renderAs='p' size={textSize} data-testid='cash-cohort-convergence-default-instruction'>
                    {t('cashCohortConvergenceRemediation:instructions.default')}
                  </Text>
                )}
                {isInstructionsPage && hasRecognizedViolations ? (
                  <div className={styles.instructions}>
                    <Text renderAs='p' size={textSize} bold data-testid='cash-cohort-convergence-instructions-app-location'>
                      {t('cashCohortConvergenceRemediation:instructions.appLocationHeader')}
                    </Text>
                    <ul className={styles.instructionList} data-testid='cash-cohort-convergence-instruction-list'>
                      {instructionKeys.map((instructionKey) => {
                        const instructionCopy = getInstructionCopy(instructionKey)

                        return (
                          <li key={instructionKey} className={styles.instructionListItem} data-testid='cash-cohort-convergence-instruction'>
                            <span className={styles.instructionIconTile} data-testid='cash-cohort-convergence-instruction-icon'>
                              <Icon name={CASH_COHORT_CONVERGENCE_INSTRUCTION_ICON_NAMES[instructionKey]} color='Black' size='Small' />
                            </span>
                            <span className={styles.instructionText}>
                              <span className={styles.instructionTitle}>{instructionCopy.title}</span>
                              <span className={styles.instructionSubtext}>{instructionCopy.subtext}</span>
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ) : null}
                {!isInstructionsPage && (
                  <div className={styles.terminalBody} data-testid='cash-cohort-convergence-terminal-body'>
                    <Text renderAs='p' size={textSize}>
                      {t('cashCohortConvergenceRemediation:terminal.body')}
                    </Text>
                    <Text renderAs='p' size={textSize}>
                      {t('cashCohortConvergenceRemediation:terminal.body2')}
                    </Text>
                  </div>
                )}
              </section>
            </Grid.Item>
          </Grid>
        </div>
        <DockedFooterPortal>
          <div className={styles.footer}>
            <Grid>
              <Grid.Item>
                <div className={styles.exitButtonWrapper}>
                  <CloseCheckout
                    kind='Primary'
                    padding='Fluid'
                    size={buttonSize}
                    testNameSpace='cash-cohort-convergence-exit'
                    redirectUrlOverride={failureCallbackUrl}
                  >
                    {t('cashCohortConvergenceRemediation:exitCheckout')}
                  </CloseCheckout>
                </div>
              </Grid.Item>
            </Grid>
            {isInstructionsPage && hasRecognizedViolations && (
              <Text renderAs='p' size='XS' color='Subtle' data-testid='cash-cohort-convergence-instructions-disclaimer'>
                {t('cashCohortConvergenceRemediation:instructions.disclaimer')}
              </Text>
            )}
            {!isInstructionsPage && (
              <Text renderAs='p' size='XS' color='Subtle' data-testid='cash-cohort-convergence-terminal-disclaimer'>
                {t('cashCohortConvergenceRemediation:terminal.disclaimer')}
              </Text>
            )}
          </div>
        </DockedFooterPortal>
      </div>
      <TrackPageViewed pageName={pageName} />
    </>
  )
}
/* eslint-enable @afterpay/i18n-require-context */

export const CashCohortConvergenceInstructionsPage: CheckoutPage = (): ReactElement => <CashCohortConvergencePage kind='instructions' />

export const CashCohortConvergenceUnavailablePage: CheckoutPage = (): ReactElement => <CashCohortConvergencePage kind='terminal' />
